import React from "react";
import { LegalLayout } from "./LegalLayout";

const LAST_UPDATED = "8 February 2026";

/**
 * Privacy Policy — UK GDPR compliant.
 * Placeholders: [BUSINESS_ENTITY], [BUSINESS_ADDRESS], [COMPANY_NUMBER], [ICO_REG_NUMBER].
 */
export const PrivacyPage = ({ t, language = "en" }) => {
  if (language === "pl") {
    return (
      <LegalLayout title="Polityka prywatności" lastUpdated={LAST_UPDATED} t={t} language={language}>
        <h2>1. Kto jest administratorem danych</h2>
        <p>
          Administratorem Twoich danych osobowych jest <strong>Worthify</strong>, prowadzona przez <strong>Worthify Ltd</strong> (Company No. <strong>SC892241</strong>), adres: Unit 08, Flexspace / Glasgow Business Park, Springhill Parkway, Baillieston, Glasgow, G69 6GA. Numer rejestracji ICO: <strong>C1956536</strong>.
        </p>
        <p>
          Kontakt w sprawach danych osobowych: <a href="mailto:support@worthify.co.uk">support@worthify.co.uk</a>.
        </p>

        <h2>2. Jakie dane zbieramy</h2>
        <h3>a) Dane podawane przez Ciebie</h3>
        <ul>
          <li><strong>Rejestracja pojazdu (UK)</strong> — wymagane do pobrania historii MOT z DVSA</li>
          <li><strong>VIN (opcjonalnie)</strong> — używane do weryfikacji zgodności z rejestracją (wykrywanie sklonowanych aut)</li>
          <li><strong>Cena ofertowa (opcjonalnie)</strong> — używana do oceny czy cena jest fair</li>
          <li><strong>Adres email</strong> — tylko jeśli kontaktujesz się z nami w sprawie wsparcia lub zwrotu</li>
        </ul>

        <h3>b) Dane zbierane automatycznie</h3>
        <ul>
          <li><strong>Adres IP</strong> — do zapobiegania nadużyciom (rate limiting) i bezpieczeństwa</li>
          <li><strong>Preferencja językowa</strong> — przechowywana w localStorage Twojej przeglądarki</li>
          <li><strong>ID sesji płatności Stripe</strong> — przekazywane przez URL po powrocie z checkoutu</li>
        </ul>

        <h3>c) Dane przekazywane przez Stripe</h3>
        <ul>
          <li>Status płatności, kwota, waluta, data transakcji — <strong>NIE przechowujemy danych Twojej karty</strong></li>
        </ul>

        <h2>3. Dlaczego przetwarzamy Twoje dane (podstawa prawna)</h2>
        <ul>
          <li><strong>Wykonanie umowy</strong> (Art. 6(1)(b) UK GDPR) — dostarczenie zamówionego raportu</li>
          <li><strong>Uzasadniony interes</strong> (Art. 6(1)(f)) — zapobieganie nadużyciom, bezpieczeństwo serwisu, ulepszanie jakości modeli AI</li>
          <li><strong>Obowiązek prawny</strong> (Art. 6(1)(c)) — przechowywanie zapisów transakcji dla celów księgowych (6 lat zgodnie z prawem UK)</li>
        </ul>

        <h2>4. Komu udostępniamy dane</h2>
        <ul>
          <li><strong>DVSA</strong> (rządowa agencja UK) — wysyłamy rejestrację, aby pobrać historię MOT</li>
          <li><strong>NHTSA vPIC</strong> (rządowa baza USA, publiczna) — wysyłamy VIN do dekodowania (tylko gdy go podasz)</li>
          <li><strong>Stripe Payments Europe Ltd</strong> — proces płatności (osobny administrator)</li>
          <li><strong>Dostawcy AI</strong> (OpenAI, Anthropic, Google) za pośrednictwem Emergent — analiza danych pojazdu; nie wysyłamy danych identyfikujących Cię osobiście, tylko anonimowe dane techniczne pojazdu</li>
          <li><strong>MongoDB Atlas</strong> (UK / EU region) — baza danych w której cachujemy wyniki analizy</li>
          <li><strong>Pexels</strong> — pobieranie zdjęć stockowych pojazdów (otrzymują tylko parametry zapytania, np. „audi q7")</li>
        </ul>
        <p>
          <strong>Nie sprzedajemy</strong> ani nie wynajmujemy Twoich danych stronom trzecim w celach marketingowych.
        </p>

        <h2>5. Transfer poza UK</h2>
        <p>
          Niektórzy nasi dostawcy (OpenAI, Anthropic — USA, Pexels) przetwarzają dane poza UK/EOG. Transfer odbywa się w oparciu o <strong>Standard Contractual Clauses</strong> zatwierdzone przez Komisję Europejską / UK ICO.
        </p>

        <h2>6. Jak długo przechowujemy dane</h2>
        <ul>
          <li><strong>Wyniki analiz pojazdów</strong> — cachowane na czas nieokreślony pod kluczem (rejestracja + język + cena ofertowa) w celu szybszego ponownego wyświetlenia. Możesz zażądać usunięcia (patrz pkt. 7).</li>
          <li><strong>Zapisy płatności</strong> — 6 lat (obowiązek księgowy UK)</li>
          <li><strong>Logi serwerowe (IP, czasy odpowiedzi)</strong> — 90 dni</li>
          <li><strong>Cache dekodowania VIN</strong> — bezterminowo (VIN nie identyfikuje Ciebie, tylko pojazd)</li>
        </ul>

        <h2>7. Twoje prawa (UK GDPR)</h2>
        <p>Masz prawo do:</p>
        <ul>
          <li><strong>Dostępu</strong> — zażądania kopii danych, jakie przetwarzamy</li>
          <li><strong>Sprostowania</strong> — poprawienia błędnych danych</li>
          <li><strong>Usunięcia</strong> ("prawo do bycia zapomnianym") — usuniemy Twoje dane oprócz tych, które musimy zachować z mocy prawa (np. faktury)</li>
          <li><strong>Ograniczenia przetwarzania</strong></li>
          <li><strong>Przenoszenia danych</strong> — otrzymasz dane w formacie JSON</li>
          <li><strong>Sprzeciwu</strong> wobec przetwarzania opartego na uzasadnionym interesie</li>
          <li><strong>Skargi</strong> do organu nadzorczego — w UK to <a href="https://ico.org.uk/" target="_blank" rel="noopener noreferrer">Information Commissioner's Office (ICO)</a>; w Polsce — Urząd Ochrony Danych Osobowych (UODO)</li>
        </ul>
        <p>
          Wniosek wyślij na: <a href="mailto:support@worthify.co.uk">support@worthify.co.uk</a>. Odpowiadamy w ciągu 30 dni.
        </p>

        <h2>8. Bezpieczeństwo</h2>
        <p>
          Stosujemy szyfrowanie HTTPS, zarządzanie kluczami w zmiennych środowiskowych, izolację baz danych i monitorowanie błędów. Płatności przetwarza Stripe — certyfikat PCI-DSS Level 1.
        </p>

        <h2>9. Pliki cookies</h2>
        <p>
          Worthify używa <strong>tylko niezbędnych cookies / localStorage</strong> do działania serwisu (zapisanie aktualnej analizy, języka, statusu odblokowania). Szczegóły w naszej <a href="/cookies">Polityce cookies</a>.
        </p>

        <h2>10. Dzieci</h2>
        <p>
          Worthify nie jest skierowany do osób poniżej 18 roku życia. Nie zbieramy świadomie danych dzieci.
        </p>

        <h2>11. Zmiany polityki</h2>
        <p>
          Aktualizacje publikujemy na <code>worthify.co.uk/privacy</code>. Ostatnia aktualizacja: <strong>{LAST_UPDATED}</strong>.
        </p>
      </LegalLayout>
    );
  }

  // English
  return (
    <LegalLayout title="Privacy Policy" lastUpdated={LAST_UPDATED} t={t} language={language}>
      <h2>1. Who is the data controller</h2>
      <p>
        The data controller is <strong>Worthify</strong>, operated by <strong>Worthify Ltd</strong> (Company No. <strong>SC892241</strong>), registered address: Unit 08, Flexspace / Glasgow Business Park, Springhill Parkway, Baillieston, Glasgow, G69 6GA. ICO registration number: <strong>C1956536</strong>.
      </p>
      <p>
        For data-protection enquiries: <a href="mailto:support@worthify.co.uk">support@worthify.co.uk</a>.
      </p>

      <h2>2. What data we collect</h2>
      <h3>a) Information you give us</h3>
      <ul>
        <li><strong>UK vehicle registration</strong> — required to fetch the MOT history from DVSA</li>
        <li><strong>VIN (optional)</strong> — used to verify it matches the registration (cloned-car detection)</li>
        <li><strong>Asking price (optional)</strong> — used to evaluate whether the price is fair</li>
        <li><strong>Email address</strong> — only if you contact us for support or a refund</li>
      </ul>

      <h3>b) Information we collect automatically</h3>
      <ul>
        <li><strong>IP address</strong> — used for abuse prevention (rate limiting) and security</li>
        <li><strong>Language preference</strong> — stored in your browser's localStorage</li>
        <li><strong>Stripe session ID</strong> — passed via URL after checkout return</li>
      </ul>

      <h3>c) Information from Stripe</h3>
      <ul>
        <li>Payment status, amount, currency, transaction date — <strong>we do NOT store your card details</strong></li>
      </ul>

      <h2>3. Why we process your data (legal basis)</h2>
      <ul>
        <li><strong>Performance of contract</strong> (Art. 6(1)(b) UK GDPR) — to deliver the report you ordered</li>
        <li><strong>Legitimate interests</strong> (Art. 6(1)(f)) — abuse prevention, service security, improving AI model quality</li>
        <li><strong>Legal obligation</strong> (Art. 6(1)(c)) — retaining transaction records for UK accounting purposes (6 years)</li>
      </ul>

      <h2>4. Who we share data with</h2>
      <ul>
        <li><strong>DVSA</strong> (UK government agency) — we send the registration to retrieve the MOT history</li>
        <li><strong>NHTSA vPIC</strong> (US public government database) — we send the VIN for decoding (only if you provide one)</li>
        <li><strong>Stripe Payments Europe Ltd</strong> — payment processing (separate data controller)</li>
        <li><strong>AI providers</strong> (OpenAI, Anthropic, Google) via Emergent — vehicle technical analysis; we do not send personally identifiable information about you, only anonymous vehicle technical data</li>
        <li><strong>MongoDB Atlas</strong> (UK / EU region) — database where we cache analysis results</li>
        <li><strong>Pexels</strong> — stock vehicle imagery (they only receive search parameters, e.g. "audi q7")</li>
      </ul>
      <p>
        We <strong>do not sell</strong> or rent your data to third parties for marketing.
      </p>

      <h2>5. International transfers</h2>
      <p>
        Some of our processors (OpenAI, Anthropic — USA; Pexels) process data outside the UK/EEA. Transfers rely on the <strong>Standard Contractual Clauses</strong> approved by the UK ICO and European Commission.
      </p>

      <h2>6. How long we keep data</h2>
      <ul>
        <li><strong>Vehicle analysis results</strong> — cached indefinitely keyed by (registration + language + asking price) for fast re-display. You may request deletion (see point 7).</li>
        <li><strong>Payment records</strong> — 6 years (UK accounting obligation)</li>
        <li><strong>Server logs (IP, response times)</strong> — 90 days</li>
        <li><strong>VIN decode cache</strong> — indefinitely (VIN identifies the vehicle, not you)</li>
      </ul>

      <h2>7. Your rights (UK GDPR)</h2>
      <p>You have the right to:</p>
      <ul>
        <li><strong>Access</strong> — request a copy of the data we hold about you</li>
        <li><strong>Rectification</strong> — correct inaccurate data</li>
        <li><strong>Erasure</strong> ("right to be forgotten") — we will delete your data except where we are legally required to keep it (e.g. invoices)</li>
        <li><strong>Restriction</strong> of processing</li>
        <li><strong>Data portability</strong> — receive your data in JSON format</li>
        <li><strong>Object</strong> to processing based on legitimate interests</li>
        <li><strong>Lodge a complaint</strong> with the supervisory authority — in the UK that's the <a href="https://ico.org.uk/" target="_blank" rel="noopener noreferrer">Information Commissioner's Office (ICO)</a></li>
      </ul>
      <p>
        Send requests to: <a href="mailto:support@worthify.co.uk">support@worthify.co.uk</a>. We respond within 30 days.
      </p>

      <h2>8. Security</h2>
      <p>
        We use HTTPS encryption, environment-variable secret management, database isolation, and error monitoring. Payments are processed by Stripe — PCI-DSS Level 1 certified.
      </p>

      <h2>9. Cookies</h2>
      <p>
        Worthify uses <strong>only essential cookies / localStorage</strong> for site functionality (saving the current analysis, language, unlock status). Details in our <a href="/cookies">Cookie Policy</a>.
      </p>

      <h2>10. Children</h2>
      <p>
        Worthify is not directed at users under 18. We do not knowingly collect data from children.
      </p>

      <h2>11. Changes to this policy</h2>
      <p>
        Updates are published at <code>worthify.co.uk/privacy</code>. Last updated: <strong>{LAST_UPDATED}</strong>.
      </p>
    </LegalLayout>
  );
};